/**
 * SkipToContent — Phase 6 a11y primitive (WCAG 2.4.1 Bypass Blocks).
 *
 * Renders a link that is visually hidden by default but becomes visible
 * when it receives keyboard focus. Activating it moves focus (and the
 * viewport) to the `<main>` content region, so keyboard users don't have
 * to Tab through the entire nav / brand header before reaching primary
 * content.
 *
 * Contract:
 *   • Must be the **first focusable element** in the tab order of any
 *     layout that wraps a `<main>` (or a `#main-content` focus target).
 *   • The target must exist somewhere on the page. In shells that own a
 *     `<main>` element, the shell itself renders `<main id="main-content">`.
 *     In `AppNav` (which is a header rendered adjacent to a per-page
 *     `<main>`), we ALSO render a `<span id="main-content" tabIndex={-1} />`
 *     immediately after the nav so the skip-link target is guaranteed to
 *     exist even if the downstream page's `<main>` lacks an id.
 *
 * Usage:
 *   <SkipToContent /> // uses default href="#main-content" + copy
 *   <SkipToContent href="#dashboard-main" label="Skip to dashboard" />
 */
import React from "react";

export default function SkipToContent({
  href = "#main-content",
  label = "Skip to main content",
  className = "",
  ...rest
}) {
  return (
    <a
      href={href}
      data-testid="skip-to-content"
      className={
        // sr-only until focused, then a solid pill sitting at the very
        // top-left of the viewport with clear focus outline.
        "sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 " +
        "focus:z-[100] focus:rounded-md focus:px-4 focus:py-2 " +
        "focus:bg-v2-accentStrong focus:text-v2-accentInk " +
        "focus:shadow-v2-lifted focus:outline-none focus:ring-2 " +
        "focus:ring-v2-accentStrong focus:ring-offset-2 " +
        (className || "")
      }
      {...rest}
    >
      {label}
    </a>
  );
}
